import React, { useEffect, useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  Link as LinkIcon,
  Globe,
  AlertTriangle,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import EventLandingPageModal from "./EventLandingPageModal";
import EventRegistrationsModal from "./EventRegistrationsModal";
import {
  listEventLandingPages,
  createEventLandingPage,
  updateEventLandingPage,
  deleteEventLandingPage,
  duplicateEventLandingPage,
} from "../../../../services/eventLandingServices";

const LAYOUT_LABELS = {
  "classic-saas": "Classic SaaS",
  "centered-big-promise": "Centered Big Promise",
  "background-card": "Background Card",
  "agenda-first": "Agenda First",
  "evergreen-on-demand": "Evergreen On-Demand",
};

const EventLandingPages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [viewing, setViewing] = useState(null);
  const [viewingRegistrations, setViewingRegistrations] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await listEventLandingPages(page, limit);
      setItems(res.data || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error("Failed to load event landing pages", err);
      toast.error("Failed to load event landing pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleCreate = async (data) => {
    try {
      await createEventLandingPage(data);
      toast.success("Event landing page created");
      setShowModal(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Create failed");
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateEventLandingPage(editing.id || editing._id, data);
      toast.success("Event landing page updated");
      setShowModal(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Update failed");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteEventLandingPage(toDelete.id || toDelete._id);
      toast.success("Deleted");
      setShowDelete(false);
      setToDelete(null);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  const handleDuplicate = async (item) => {
    try {
      await duplicateEventLandingPage(item.id || item._id);
      toast.success("Duplicated");
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error("Duplicate failed");
    }
  };

  const handleCopyUrl = (item) => {
    const url = `${window.location.origin}/event/${item.landingPageSlug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const date = new Date(d);
      const datePart = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timePart = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart} · ${timePart}`;
    } catch {
      return String(d);
    }
  };

  return (
    <div
      className="min-h-screen text-white p-3 md:p-6"
      style={{ backgroundColor: "#001233" }}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              Event Landing Pages
            </h1>
            <p className="text-sm md:text-base text-gray-400 mt-1">
              Create high-converting landing pages with multiple layouts
            </p>
          </div>
          <button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            className="px-4 md:px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg transition-all font-semibold hover:shadow-lg hover:shadow-purple-500/25 flex items-center justify-center space-x-2 text-sm md:text-base"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            <span>Create Event</span>
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center text-gray-300">
            Loading...
          </div>
        )}

        {/* Empty */}
        {!loading && items.length === 0 && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center">
            <Globe className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No event landing pages yet
            </h3>
            <p className="text-gray-400 mb-6">
              Create your first event landing page to get started
            </p>
            <button
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg transition-all font-semibold hover:shadow-lg hover:shadow-purple-500/25 inline-flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Create Event</span>
            </button>
          </div>
        )}

        {/* List */}
        {!loading && items.length > 0 && (
          <div className="grid gap-4">
            {items.map((item) => (
              <div
                key={item.id || item._id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-2 truncate">
                      {item.yourName || "Untitled Event"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-3">
                      <span className="px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded">
                        {LAYOUT_LABELS[item.layoutType] || item.layoutType}
                      </span>
                      <span>{formatDate(item.scheduledStartTime)}</span>
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                        {item.format || "live"}
                      </span>
                      {item.totalCapacity !== undefined && (
                        <span>
                          {item.remainingSpots ?? 0} / {item.totalCapacity ?? 0} spots remaining
                        </span>
                      )}
                    </div>
                    {item.landingPageSlug && (
                      <p className="text-gray-400 text-sm flex items-center gap-1 truncate">
                        <LinkIcon className="h-3.5 w-3.5" />
                        /event/{item.landingPageSlug}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      title="Preview"
                      onClick={() => setViewing(item)}
                      className="p-2 rounded-md text-white hover:text-white/80 hover:bg-white/5"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      title="View Registrations"
                      onClick={() => setViewingRegistrations(item)}
                      className="p-2 rounded-md text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                    >
                      <Users className="h-4 w-4" />
                    </button>
                    <button
                      title="Copy URL"
                      onClick={() => handleCopyUrl(item)}
                      className="p-2 rounded-md text-white hover:text-white/80 hover:bg-white/5"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      title="Edit"
                      onClick={() => {
                        setEditing(item);
                        setShowModal(true);
                      }}
                      className="p-2 rounded-md text-white hover:text-white/80 hover:bg-white/5"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      title="Duplicate"
                      onClick={() => handleDuplicate(item)}
                      className="p-2 rounded-md text-white hover:text-white/80 hover:bg-white/5"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      title="Delete"
                      onClick={() => {
                        setToDelete(item);
                        setShowDelete(true);
                      }}
                      className="p-2 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-gray-400 text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <EventLandingPageModal
          isOpen={showModal}
          isEdit={!!editing}
          initialData={editing}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSubmit={editing ? handleUpdate : handleCreate}
        />
      )}

      {/* View Modal (read-only) */}
      {viewing && (
        <EventLandingPageModal
          isOpen={!!viewing}
          isEdit={false}
          readOnly
          initialData={viewing}
          onClose={() => setViewing(null)}
          onSubmit={() => setViewing(null)}
        />
      )}

      {/* Registrations Modal */}
      {viewingRegistrations && (
        <EventRegistrationsModal
          event={viewingRegistrations}
          onClose={() => setViewingRegistrations(null)}
        />
      )}

      {/* Delete Confirm */}
      {showDelete && toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-[#0a1a3a] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">
                  Delete event landing page?
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  This will permanently delete{" "}
                  <span className="text-white">{toDelete.yourName}</span>. This
                  action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDelete(false);
                  setToDelete(null);
                }}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventLandingPages;
